import { SingleSelectControl } from "@dgs/core";
import { useFormikContext } from "formik";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FilterItemFields } from "../FilterItemFields";
import { useFilter } from "../FilterProvider";
import { FilterFormValues, FilterItemFormValues, FilterableItem } from "../filterTypes";

const Wrapper = styled.div`
	padding: ${({ theme }) => theme.spacing(4)};
`;

const Line = styled.div`
	height: 1px;
	background-color: ${({ theme }) => theme.colors.palette.grey[20]};
	margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

interface Props {
	name: string;
	unshift: (x: FilterItemFormValues) => void;
	remove: (i: number) => void;
}

export const AdvancedEventFilterForm: FC<Props> = ({ name, unshift, remove }) => {
	const { t } = useTranslation();
	const { options } = useFilter();
	const { values } = useFormikContext<FilterFormValues>();
	const [search, setSearch] = useState("");

	const mapInitialFilterItem = useCallback((value: FilterableItem): FilterItemFormValues => {
		return {
			entity: value.entity,
			field: value.field,
			operators: value.operators,
			operator: null,
			value: null,
		};
	}, []);

	const filteredOptions: FilterableItem[] = useMemo(() => {
		return options.filter((o) => {
			if (values.filterItems.find((item) => item.entity === o.entity && item.field === o.field)) {
				return false;
			}

			const label = t(`${o.entity}.${o.field}`);
			return label.toLowerCase().includes(search.toLowerCase());
		});
	}, [options, values.filterItems, t, search]);

	return (
		<Wrapper>
			<SingleSelectControl
				onChange={async (e) => {
					if (e.target.value) {
						const empty = e.target.value;
						unshift(empty);
					}
				}}
				label={t("Attribute")}
				value={null as FilterItemFormValues | null}
				name="attributeSelect"
				onClear={() => void 0}
				options={filteredOptions.map((o) => mapInitialFilterItem(o))}
				getLabel={(o: FilterItemFormValues) => t(`${o.entity}.${o.field}`)}
				search={search}
				onSearch={setSearch}
			/>
			<Line />
			{values.filterItems.map((item, index) => (
				<FilterItemFields
					key={`${item.entity}.${item.field}.${index}`}
					namePrefix={`${name}.${index}`}
					filterItem={item}
					withDivider={index + 1 < values.filterItems.length}
					onDelete={() => remove(index)}
				/>
			))}
		</Wrapper>
	);
};
